"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  parseResponse,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";
import type { Schemas } from "@softmax/observatory-client/types";
import {
  sessionChangeEvent,
  sessionChannel,
  sessionEventSource,
} from "./session-actions";
import {
  initialSessionState,
  sessionChangeTrigger,
  type BrowserSession,
  type SessionState,
} from "./browser-session-state";

export function useBrowserSessionOwner({
  initialSession,
}: {
  readonly initialSession?: (BrowserSession | null) | undefined;
}) {
  const [state, setState] = useState<SessionState>(() =>
    initialSessionState(initialSession),
  );
  const activeRequest = useRef<{
    readonly controller: AbortController;
    readonly promise: Promise<void>;
  } | null>(null);
  const needsInitialRead = useRef(initialSession === undefined);
  const refresh = useCallback(() => {
    if (activeRequest.current) return activeRequest.current.promise;

    const controller = new AbortController();
    const promise = fetch("/api/auth/session", {
      cache: "no-store",
      signal: withRequestDeadline(
        controller.signal,
        DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
      ),
    })
      .then((response) =>
        parseResponse<Schemas["WebsiteSessionResponse"] | null>(response),
      )
      .then(
        (session) => {
          if (controller.signal.aborted) return;

          setState(
            session
              ? { kind: "authenticated", session, error: null }
              : { kind: "anonymous", session: null, error: null },
          );
        },
        (error: Error) => {
          if (controller.signal.aborted) return;

          if (
            error instanceof ApiError &&
            (error.status === 401 || error.status === 403)
          ) {
            setState({ kind: "anonymous", session: null, error: null });
            return;
          }

          setState((previous) => ({
            kind: "unavailable",
            session: previous.session,
            error,
          }));
        },
      )
      .finally(() => {
        if (activeRequest.current?.controller === controller)
          activeRequest.current = null;
      });
    activeRequest.current = { controller, promise };
    return promise;
  }, []);
  useEffect(() => {
    if (needsInitialRead.current) void refresh();

    const channel = new BroadcastChannel(sessionChannel);
    const onChange = (event: Event) => {
      const change = sessionChangeTrigger(event, sessionEventSource);

      if (change === null) return;

      activeRequest.current?.controller.abort();
      activeRequest.current = null;

      if (change.trigger === "signed-out" || change.trigger === "signout")
        setState({ kind: "anonymous", session: null, error: null });
      else if (change.trigger === "invalidated")
        setState({ kind: "pending", session: null, error: null });

      void refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    channel.addEventListener("message", onChange);
    // A full document load may follow an OAuth callback that changed the shared cookie.
    // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel messages stay on this origin and take no target origin.
    channel.postMessage({ event: "session", data: { trigger: "getSession" } });
    window.addEventListener(sessionChangeEvent, onChange);
    window.addEventListener("online", onVisible);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      activeRequest.current?.controller.abort();
      activeRequest.current = null;
      channel.close();
      window.removeEventListener(sessionChangeEvent, onChange);
      window.removeEventListener("online", onVisible);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);
  return { ...state, refresh };
}
